import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Nav, Container, Button, Badge, Form } from 'react-bootstrap';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';
import { setOnlineOrdersPaused, setMinDeliveryTime, setShowMinDeliveryTime } from '../../store/slices/uiSlice';
import { selectActiveCallCount, selectPendingOnlineOrderCount } from '../../store/selectors';
import { isAdmin } from '../../utils/auth';
import { toggleOnlineOrders, updateMinDeliveryTime } from '../../services/api';
import PrinterIndicators from './PrinterIndicators';

export default function Header() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userName = useAppSelector((s) => s.auth.userName);
  const isConnected = useAppSelector((s) => s.auth.isConnected);
  const activeAgent = useAppSelector((s) => s.ui.activeAgent);
  const activeCallCount = useAppSelector(selectActiveCallCount);
  const pendingOnlineOrderCount = useAppSelector(selectPendingOnlineOrderCount);
  const onlineOrdersPaused = useAppSelector((s) => s.ui.onlineOrdersPaused);
  const minDeliveryTime = useAppSelector((s) => s.ui.minDeliveryTime);
  const showMinDeliveryTime = useAppSelector((s) => s.ui.showMinDeliveryTime);

  const [toggling, setToggling] = useState(false);
  const [localMinTime, setLocalMinTime] = useState<string>('');
  const minTimeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync Redux → local input
  useState(() => { setLocalMinTime(minDeliveryTime != null ? String(minDeliveryTime) : ''); });
  useEffect(() => { setLocalMinTime(minDeliveryTime != null ? String(minDeliveryTime) : ''); }, [minDeliveryTime]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleMinTimeChange = (val: string) => {
    setLocalMinTime(val);
    if (minTimeRef.current) clearTimeout(minTimeRef.current);
    minTimeRef.current = setTimeout(async () => {
      let minutes = val.trim() ? parseInt(val, 10) : null;
      if (minutes !== null && isNaN(minutes)) return;
      // Clamp to [0, 90] — typed-in values bypass the input's max attr.
      if (minutes !== null) minutes = Math.max(0, Math.min(90, minutes));
      try {
        await updateMinDeliveryTime({ minutes });
        dispatch(setMinDeliveryTime(minutes));
        if (minutes !== null) setLocalMinTime(String(minutes));
      } catch { /* ignore */ }
    }, 800);
  };

  const handleToggleShowMinTime = async () => {
    const newVal = !showMinDeliveryTime;
    dispatch(setShowMinDeliveryTime(newVal));
    try {
      await updateMinDeliveryTime({ show: newVal });
    } catch { /* ignore */ }
  };

  const handleToggleOnlineOrders = async () => {
    setToggling(true);
    try {
      const newPaused = !onlineOrdersPaused;
      await toggleOnlineOrders(newPaused);
      dispatch(setOnlineOrdersPaused(newPaused));
    } catch {
      // ignore
    }
    setToggling(false);
  };

  const reservationsEnabled = useAppSelector((s) => s.ui.reservationsEnabled);
  const pendingReservationCount = useAppSelector((s) => s.ui.pendingReservationCount);
  const view = useAppSelector((s) => s.ui.uiVisibleComponents);
  const tabletView = view.ui_tablet_view;
  const admin = isAdmin();

  const navItems = [
    // 'Telefon' was a duplicate entry point — the HalloChef brand already
    // navigates to /order, so removing it here cleans up the nav bar.
    // Per-user nav visibility: admins always see everything; non-admins see a
    // route only if the matching ui_view_* flag is set (admin-controlled).
    ...((admin || view.ui_view_deliveries) ? [{ path: '/deliveries', label: 'Narudžbe', icon: '📋', badge: pendingOnlineOrderCount > 0 ? pendingOnlineOrderCount : null, badgeVariant: 'warning' as const }] : []),
    ...((admin || view.ui_view_calls) ? [{ path: '/calls', label: 'Pozivi', icon: '📞', badge: activeCallCount > 0 ? activeCallCount : null, badgeVariant: 'danger' as const }] : []),
    ...(reservationsEnabled ? [{ path: '/reservations', label: 'Rez', icon: '📅', badge: pendingReservationCount > 0 ? pendingReservationCount : null, badgeVariant: 'warning' as const }] : []),
    ...((admin || view.ui_view_handoff) ? [{ path: '/handoff', label: 'Primopredaja', icon: '🤝' }] : []),
    ...((admin || view.ui_view_menu) ? [{ path: '/menu', label: 'Meni', icon: '🍽️' }] : []),
    ...((tabletView || admin) ? [{ path: '/tables', label: 'Stolovi', icon: '🪑' }] : []),
    ...((tabletView || admin) ? [{ path: '/local-orders', label: 'Lokal', icon: '🧾' }] : []),
    ...((admin || view.ui_view_deliverymen) ? [{ path: '/deliverymen', label: 'Dostava', icon: '🛵' }] : []),
    ...(isAdmin() ? [
      { path: '/stats', label: 'Stats', icon: '📊' },
      { path: '/trends', label: 'Trends', icon: '📈' },
      { path: '/ivr', label: 'IVR', icon: '🎙️' },
      { path: '/settings', label: 'Postavke', icon: '⚙️' },
    ] : []),
  ];

  const displayName = activeAgent ? `${userName} (${activeAgent})` : userName;

  const onlineActive = !onlineOrdersPaused;

  return (
    <>
    <Navbar bg="dark" variant="dark" expand="md" sticky="top">
      <Container fluid>
        {/* Brand goes to the user's default view: tablet (waiter) users land on
            the floor plan, everyone else on the phone-order screen. */}
        <Navbar.Brand onClick={() => navigate(tabletView ? '/tables' : '/order')} style={{ cursor: 'pointer' }}>
          HalloChef
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="main-navbar" />
        <Navbar.Collapse id="main-navbar">
          <Nav className="me-auto">
            {navItems.map((item) => (
              <Nav.Link
                key={item.path}
                active={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                title={item.label}
              >
                {item.icon ? (
                  <>
                    <span style={{ fontSize: '1.1rem', marginRight: 4 }}>{item.icon}</span>
                    {/* Icons-only on the inline bar (md–lg, incl. tablet portrait/landscape) where
                        horizontal room is tight; phones (collapsed menu) and xl+ show labels. */}
                    <span className="d-inline d-md-none d-xl-inline">{item.label}</span>
                  </>
                ) : (
                  item.label
                )}
                {'badge' in item && item.badge != null && (
                  <>
                    {' '}
                    <Badge bg={item.badgeVariant} pill>{item.badge}</Badge>
                  </>
                )}
              </Nav.Link>
            ))}
          </Nav>
          <Nav className="align-items-md-center flex-wrap gap-2 gap-md-0">
            <div className="d-flex align-items-center flex-wrap gap-2 me-md-3 py-2 py-md-0">
              {(admin || view.ui_view_min_delivery_time) && (
              <div className="hc-nav-pill" title="Min. vrijeme dostave (min) — prikazano kupcima">
                <button
                  type="button"
                  onClick={handleToggleShowMinTime}
                  title={showMinDeliveryTime ? 'Sakrij od kupaca' : 'Prikaži kupcima'}
                  className="hc-nav-pill__icon-btn"
                  data-off={!showMinDeliveryTime}
                >
                  {showMinDeliveryTime ? '👁' : '👁‍🗨'}
                </button>
                <input
                  type="number"
                  min={0}
                  max={90}
                  step={5}
                  value={localMinTime}
                  onChange={(e) => handleMinTimeChange(e.target.value)}
                  placeholder="min"
                  title="Min. vrijeme dostave (min)"
                />
                <span style={{ opacity: 0.7 }}>min</span>
              </div>
              )}
              {(admin || view.ui_view_online_toggle) && (
              <div className={`hc-nav-pill ${onlineActive ? 'hc-nav-pill--success' : 'hc-nav-pill--danger'}`}>
                <span>Online narudžbe</span>
                <Form.Check
                  type="switch"
                  id="online-orders-toggle"
                  checked={onlineActive}
                  onChange={handleToggleOnlineOrders}
                  disabled={toggling}
                  className="mb-0"
                />
              </div>
              )}
            </div>
            <div className="d-flex align-items-center flex-wrap gap-2 me-md-3 py-1 py-md-0">
              <PrinterIndicators />
            </div>
            <Navbar.Text className="me-md-3">
              {isConnected ? '🟢' : '🔴'} {displayName}
            </Navbar.Text>
            <div className="d-flex gap-2 py-2 py-md-0">
              <Button variant="outline-light" size="sm" onClick={handleLogout}>
                Odjava
              </Button>
            </div>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
    </>
  );
}
